import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '../../shared/services/api.service';
import { AuthService } from '../../core/auth/auth.service';
import { ConsentFlowService } from '../../shared/services/consent-flow.service';
import { maskIdNumberForDisplay, maskCellNumberForDisplay } from '../../shared/utils/id-number-display';

@Component({
  selector: 'app-digital-onboarding-results',
  templateUrl: './digital-onboarding-results.component.html',
  styleUrls: ['./digital-onboarding-results.component.css'],
})
export class DigitalOnboardingResultsComponent implements OnInit {
  loading = true;
  requestId = '';
  dovs: any = null;
  private isAdmin = false;
  private resolvingConsentPrompt = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService,
    private snackBar: MatSnackBar,
    private auth: AuthService,
    private consentFlowService: ConsentFlowService
  ) {}

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe((user) => {
      this.isAdmin = user?.role === 'admin';
    });
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';
    if (!this.requestId) {
      this.back();
      return;
    }
    void this.loadWithConsent();
  }

  private async loadWithConsent(): Promise<void> {
    this.loading = true;
    try {
      const consentAccepted = await this.consentFlowService.ensureConsentAccepted();
      if (!consentAccepted) {
        this.loading = false;
        this.back();
        return;
      }
      this.loadData();
    } catch {
      this.loading = false;
      this.snackBar.open('An error occurred while processing POPIA acknowledgement', 'Close', {
        duration: 5000,
      });
    }
  }

  private loadData(): void {
    this.loading = true;
    this.api.getRequestData(this.requestId).subscribe({
      next: (res: any) => {
        this.dovs = res?.dovs || (Array.isArray(res?.data) ? res.data[0] : null) || null;
        this.loading = false;
        if (!this.dovs) {
          this.snackBar.open('No DOVS result payload found', 'Close', { duration: 4000 });
        }
      },
      error: (err) => {
        this.loading = false;
        if (this.consentFlowService.isConsentRequiredError(err)) {
          void this.promptForConsentAndRetryDataLoad();
          return;
        }
        this.snackBar.open(err?.error?.error?.message || 'Failed to load results', 'Close', {
          duration: 5000,
        });
      },
    });
  }

  private async promptForConsentAndRetryDataLoad(): Promise<void> {
    if (this.resolvingConsentPrompt) return;
    this.resolvingConsentPrompt = true;
    try {
      this.loading = false;
      const consentAccepted = await this.consentFlowService.ensureConsentAccepted();
      if (!consentAccepted) {
        this.back();
        return;
      }
      this.loadData();
    } catch {
      this.loading = false;
      this.snackBar.open('An error occurred while processing POPIA acknowledgement', 'Close', {
        duration: 5000,
      });
    } finally {
      this.resolvingConsentPrompt = false;
    }
  }

  back(): void {
    const base = this.isAdmin ? '/admin/digital-onboarding' : '/user/digital-onboarding';
    this.router.navigate([base]);
  }

  displayName(): string {
    const parts = [
      this.dovs?.consumer?.titleDesc,
      this.dovs?.consumer?.firstName || this.dovs?.dovDetails?.name,
      this.dovs?.consumer?.surname || this.dovs?.dovDetails?.surname,
    ].filter((p) => !!p && String(p).trim());
    return parts.length ? parts.join(' ') : '—';
  }

  formatId(value: string | null | undefined): string {
    return maskIdNumberForDisplay(value) || '—';
  }

  formatCell(value: string | null | undefined): string {
    return maskCellNumberForDisplay(value) || '—';
  }

  formatCoords(lat: string | null | undefined, lng: string | null | undefined): string {
    const a = (lat || '').trim();
    const b = (lng || '').trim();
    if (!a && !b) return '—';
    if (a && b && a === b) return a;
    if (a && b) return `${a}, ${b}`;
    return a || b;
  }

  photoSrc(value: string | null | undefined): string | null {
    if (!value) return null;
    if (value.startsWith('data:')) return value;
    return `data:image/jpeg;base64,${value}`;
  }
}
